<template>
    <div class="product-map">
        <div v-if="mapActive" id="airportProductsMap" ref="mapContainer" class="map-container"></div>
        <div v-if="!mapActive && blok.openMapBtn && blok.mapPlaceholderImage?.filename" class="map-placeholder">
            <NuxtImg
                v-if="blok.mapPlaceholderImage?.filename"
                :src="blok.mapPlaceholderImage.filename"
                :title="blok.mapPlaceholderImage.title"
                :alt="blok.mapPlaceholderImage.alt"
                loading="lazy"
                provider="storyblok"
                format="webp"
                densities="x1"
            />
        </div>
        <div v-if="blok.openMapBtn" class="map-btn" :class="{ 'map-active': mapActive }">
            <button v-if="!mapActive" @click="openMap()">
                <NuxtIcon name="ri:map-2-line" />
                View on Map
            </button>
            <button v-else @click="closeMap()">
                <NuxtIcon name="ri:close-line" class="close-icon" />
                <span>Close map</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader';
import Button from '@nuxt-monorepo/ui/components/Button.vue';

const { blok } = defineProps<{ blok: any }>();

const config: any = useRuntimeConfig();
let googleInitLoad: Loader | null = null;
let coreLib: google.maps.CoreLibrary | null = null;
let mapsLib: google.maps.MapsLibrary | null = null;
let markerLib: google.maps.MarkerLibrary | null = null;
const mapContainer = ref<HTMLDivElement>();
let mapInstance: google.maps.Map | null = null;
const mapActive = ref(!blok.openMapBtn);

const mapMarkers: any = ref<google.maps.marker.AdvancedMarkerElement[]>([]);

const openMap = () => {
    mapActive.value = true;
    initGoogleMaps();
};

const closeMap = () => {
    mapActive.value = false;
};

function initGoogleMaps() {
    if (import.meta.client) {
        googleInitLoad = new Loader({
            apiKey: config?.public.GOOGLE_API_KEY,
            version: config?.public.GOOGLE_API_VERSION,
            language: 'en',
            libraries: ['core', 'maps', 'marker'],
        });

        // Default Map Location
        const mapOptions = {
            mapId: config?.public.GOOGLE_MAP_ID,
            center: {
                lat: Number(blok.Latitude),
                lng: Number(blok.Longitude),
            },
            zoom: Number(blok.Zoom) ?? 14,
        };

        googleInitLoad.load().then(async () => {
            // load libs
            coreLib = (await google.maps.importLibrary('core')) as google.maps.CoreLibrary;
            mapsLib = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
            markerLib = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

            // create map and place markers for airport & products
            mapInstance = new mapsLib.Map(mapContainer.value as HTMLElement, mapOptions);

            placeMarkers();
            centerMap();
        });
    }
}

// collect marker data for Airport & Product
const markersData = computed(() => {
    const data: any = [];

    blok.Markers?.forEach((product: any) => {
        data.push({
            type: product.Type,
            position: {
                lat: Number(product.Latitude),
                lng: Number(product.Longitude),
            },
            title: product.Title,
            icon: product.Icon?.filename || null,
        });
    });

    data.push({
        type: 'airport',
        position: {
            lat: Number(blok.Latitude),
            lng: Number(blok.Longitude),
        },
        icon: blok.Icon?.filename || null,
    });
    return data;
});

function placeMarkers() {
    if (mapsLib && mapInstance && markerLib) {
        // place product markers
        markersData.value.forEach((markerData) => {
            const markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
                map: mapInstance,
                position: markerData.position,
                title: markerData.title,
            };

            if (markerData.icon) {
                const markerIcon = document.createElement('img');
                markerIcon.src = markerData.icon;
                markerIcon.style.maxHeight = '34px';
                markerOptions.content = markerIcon;
            }

            const marker = new (markerLib as google.maps.MarkerLibrary).AdvancedMarkerElement(markerOptions);
            mapMarkers.value.push(marker);

            marker.addListener('click', () => {});
        });
    }
}

// center map to fit markers
function centerMap() {
    if (mapInstance && coreLib) {
        if (mapMarkers.value.length < 2) {
            mapInstance.setCenter(mapMarkers.value[0]?.position);
            mapInstance.setZoom(15);
        } else {
            const bounds = new coreLib.LatLngBounds();

            mapMarkers.value.forEach((marker: any) => {
                if (marker.position) {
                    bounds.extend(marker.position);
                }
            });

            mapInstance.fitBounds(bounds);
        }
    }
}

onMounted(() => {
    if (!blok.openMapBtn) {
        initGoogleMaps();
    }
});
</script>

<style lang="postcss" scoped>
.product-map {
    @apply relative w-full;

    &:not(:has(button.gm-fullscreen-control)) {
        .map-btn.map-active {
            @apply max-sm:-right-14 max-sm:w-[120px];
            button {
                @apply max-sm:px-1;
            }
            svg {
                @apply max-sm:mr-0;
            }
            span {
                @apply max-sm:inline-block;
            }
        }
    }

    .map-placeholder {
        @apply flex justify-center;
        img {
            @apply h-[140px] w-[350px] md:w-[500px] object-cover rounded-lg;
        }
    }
    .map-btn {
        @apply max-md:w-[200px] absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4;

        &.map-active {
            @apply max-sm:right-6 max-md:w-auto left-auto right-0 top-8;

            button {
                @apply text-tertiary bg-white border border-tertiary;
                @apply max-sm:text-base max-sm:flex max-sm:items-center max-sm:justify-center;
                &:hover {
                    @apply bg-tertiary text-white;
                }
                svg {
                    @apply max-sm:mr-0;
                }
                span {
                    @apply max-sm:hidden;
                    &.iconify {
                        @apply max-sm:inline-block;
                    }
                }
            }
        }

        button {
            @apply bg-tertiary text-white font-bold text-lg py-2 px-6 rounded flex justify-center items-center gap-x-1;
            &:hover {
                @apply bg-[#f8791e];
            }

            .close-icon {
                @apply h-5 w-5;
            }
        }
    }
}
.map-container {
    @apply w-full h-full min-h-[400px];
}
</style>
